import { createContext, ReactNode, useEffect, useState } from "react";
import { ICoord, ICoords } from "../@types/GoogleMaps/ICoord";
import { IOptionProps } from "../@types/reactSelect/IOption";
import Constants from "../constants/Index";
import { DateRange } from "react-day-picker";
import axios from "../services/axios";
import { useAuth } from "../hooks/useAuth";
import { IDailySnapshot } from "../@types/Dashboard/IDailySnapshot";
import { IFarmAPIResponse } from "../@types/refact/IFarmAPI";
import { useNavigate } from "react-router-dom";

type FilterContextProviderProps = {
  children: ReactNode;
};

type ICustomFarm = {
  id: string;
  initials: string;
  coords: ICoords;
  name: string;
  bgColor: string;
};

type ICustomBatch = {
  initials: string;
  coords: ICoord;
  id: string;
  farmId: string;
  areaId: string;
  capacityRate: number;
  microAreaId: string;
};

type ICustomMicroAreaFiltered = {
  id: string;
  areaId: string;
  coords: ICoords;
  initials: string;
  microAreaRoute: {
    current: boolean;
    batch: {
      id: string;
      initials: string;
    };
  };
};

type ICustomArea = {
  id: string;
  initials: string;
  coords: ICoords;
  name: string;
  bgColor: string;
  farmId: string;
};

export type IOptionsSideBarLinks =
  'INVENTORY' |
  'DASHBOARD' |
  'MAPA' |
  'MANAGER_SYSTEM' |
  'LOGOUT'

export const optionsSideBar: IOptionsSideBarLinks[] = ['DASHBOARD', 'MAPA', 'MANAGER_SYSTEM', 'LOGOUT', 'INVENTORY'];

export type IOptionsDashboards =
  null |
  'CARBON' |
  'EXPORT' |
  'BATCHES' |
  'CATTLE_MAP' |
  'SUPPLEMENTATION' |
  'MANAGEMENT' |
  'MANAGEMENT_PASTURE'

export type IOptionsSystemManagerLinks =
  null |
  'PROPERTIES_AREAS' |
  'PASTURE_EVALUATION' |
  'TASK_EVALUATION' |
  'CREATE_BATCH' |
  'PASTURE_MANAGEMENT' |
  'MANAGER_EVALUATORS' | // TODO move to PASTURE_MANAGEMENT
  'CATTLE_MAP' |
  'PRODUCTS' |
  'SALTERS' |
  'STOCK' |
  'PROFILES_USERS' |
  'FAILURES_SYNC' // TODO move to TASK_EVALUATION

type valuesContext = {
  farmFilter: ICustomFarm;
  areaFilter: ICustomArea;
  microAreaFilter: ICustomMicroAreaFiltered;
  hideMicroAreaFilter: boolean;
  hideBatchFilter: boolean;
  microAreaDefaultOption: IOptionProps;
  batchDefaultOption: IOptionProps;
  sideBarOptionSelected: IOptionsSideBarLinks;
  defaultBGFarm: string;
  batchFilter: ICustomBatch;
  modalInventoryTaskStop: boolean;
  setModalInventoryTaskStop: (s: boolean) => void;
  modalCalendarFilter: boolean;
  setModalCalendarFilter: (s: boolean) => void;
  setFarmFilter: (s: ICustomFarm | undefined) => void;
  setAreaFilter: (s: ICustomArea | undefined) => void;
  setMicroArea: (s: ICustomMicroAreaFiltered | undefined) => void;
  setBatchFilter: (s: ICustomBatch | undefined) => void;
  setHideMicroAreaFilter: (s: boolean) => void;
  setHideBatchFilter: (s: boolean) => void;
  setMicroAreaDefaultOption: (s: IOptionProps | undefined) => void;
  setBatchDefaultOption: (s: IOptionProps | undefined) => void;
  setSideBarOptionSelected: (value: IOptionsSideBarLinks) => void;
  systemManager: IOptionsSystemManagerLinks;
  setSystemManager: (value: IOptionsSystemManagerLinks) => void;
  dashboard: IOptionsDashboards;
  setDashboard: (value: IOptionsDashboards) => void;
  rangeDays: DateRange | undefined;
  setRangeDays: (value: DateRange | undefined) => void;
  selectedDates: DateRange | undefined;
  setSelectedDates: (value: DateRange | undefined) => void;
  viewRangeDays: DateRange | undefined;
  setViewRangeDays: (value: DateRange | undefined) => void;
  balanceAnimal: IBalanceAnimal[];
  setBalanceAnimal: (value: IBalanceAnimal[]) => void;
  balance: IBalance[];
  setBalance: (value: IBalance[]) => void;
  batchCategory: IBatchCategory;
  setBatchCategory: (value: IBatchCategory) => void;
  snapshot: IDailySnapshot[];
  setSnapshot: (value: IDailySnapshot[]) => void;
  snapshotMicroAreaMetrics: ISnapshotMicroArea;
  setSnapshotMicroAreaMetrics: (value: ISnapshotMicroArea) => void;
  snapshotMicroArea: ISnapshotGeneral[];
  setSnapshotMicroArea: (value: ISnapshotGeneral[]) => void;
  balanceCapacity: IBalanceCapacity[];
  setBalanceCapacity: (value: IBalanceCapacity[]) => void;
  areasIdsFilter: IOptionProps[];
  setAreasIdsFilter: (value: IOptionProps[]) => void;
  farmIdsFilter: IOptionProps[];
  setFarmIdsFilter: (value: IOptionProps[]) => void;
  focusLastDay: boolean;
  setFocusLastDay: (s: boolean) => void;
  pastureManagementHistory: boolean;
  setPastureManagementHistory: (s: boolean) => void;
  onBoardingDate: Date | undefined;
  setOnBoardingDate: (s: Date | undefined) => void;
  selectRangeDate: number;
  setSelectRangeDate: (value: number) => void;
  reloadTasks: number;
  setReloadTasks: (value: number) => void;
  responseFarms: IFarmAPIResponse[];
  setResponseFarms: (value: IFarmAPIResponse[]) => void;
  responseArea: IArea[];
  setResponseArea: (value: IArea[]) => void;
  paddockMapCatalogLoadError: boolean;
  setPaddockMapCatalogLoadError: (value: boolean) => void;
};

export const FilterContext = createContext({} as valuesContext);

export const FilterContextProvider = ({ children }: FilterContextProviderProps) => {
  const navigate = useNavigate();
  const [farm, setFarm] = useState<ICustomFarm | undefined>(undefined);
  const [area, setArea] = useState<ICustomArea | undefined>();
  const [microArea, setMicroArea] = useState<ICustomMicroAreaFiltered | undefined>(undefined);
  const [batch, setBatch] = useState<ICustomBatch | undefined>(undefined);

  const [microAreaDefaultOption, setMicroAreaDefaultOption] = useState<IOptionProps | undefined>(undefined);
  const [batchDefaultOption, setBatchDefaultOption] = useState<IOptionProps | undefined>(undefined);
  const [option, setOption] = useState<IOptionsSideBarLinks>('MAPA');
  const [systemManager, setSystemManager] = useState<IOptionsSystemManagerLinks>(null);
  const [dashboard, setDashboard] = useState<IOptionsDashboards>(null);
  const [defaultBGFarm, setDefaultBGFarm] = useState<string>("");
  const [areaFilter, setAreaFilter] = useState<boolean>(false);
  const [lotsFilter, setLotsFilter] = useState<boolean>(false);

  const [modalCalendarFilter, setModalCalendarFilter] = useState<boolean>(false);
  const [modalInventoryTaskStop, setModalInventoryTaskStop] = useState(false);

  const [balance, setBalance] = useState<IBalance[]>();
  const [snapshot, setSnapshot] = useState<IDailySnapshot[]>();
  const [snapshotMicroArea, setSnapshotMicroArea] = useState<ISnapshotGeneral[]>();
  const [snapshotMicroAreaMetrics, setSnapshotMicroAreaMetrics] = useState<ISnapshotMicroArea>();
  const [balanceAnimal, setBalanceAnimal] = useState<IBalanceAnimal[]>();
  const [balanceCapacity, setBalanceCapacity] = useState<IBalanceCapacity[]>();
  const [batchCategory, setBatchCategory] = useState<IBatchCategory>();
  const [areasIdsFilter, setAreasIdsFilter] = useState<IOptionProps[]>([]);
  const [farmIdsFilter, setFarmIdsFilter] = useState<IOptionProps[]>([]);
  const [focusLastDay, setFocusLastDay] = useState<boolean>(false);
  const [pastureManagementHistory, setPastureManagementHistory] = useState<boolean>(false);
  const [rangeDays, setRangeDays] = useState<DateRange | undefined>(undefined);
  const [selectedDates, setSelectedDates] = useState<DateRange | undefined>(undefined);
  const [viewRangeDays, setViewRangeDays] = useState<DateRange | undefined>(undefined);
  const [onBoardingDate, setOnBoardingDate] = useState<Date | undefined>(undefined);
  const [selectRangeDate, setSelectRangeDate] = useState(0);
  const [reloadTasks, setReloadTasks] = useState(0);
  const [responseFarms, setResponseFarms] = useState<IFarmAPIResponse[]>([]);
  const [responseArea, setResponseArea] = useState<IArea[]>([]);
  const [paddockMapCatalogLoadError, setPaddockMapCatalogLoadError] =
    useState(false);
  const { user } = useAuth();

  function getRandomColor() {
    const colors: Array<string> = Object.values(Constants.GRAZINGMAP_COLORS);
    return colors[Math.floor(Math.random() * colors.length)];
  }

  useEffect(() => {
    setDefaultBGFarm((prevColor) => (prevColor ? prevColor : getRandomColor()));
  }, []);

  useEffect(() => {
    if (user.username) {
      axios.get("/onboarding")
        .then(({ data }) => {
          if (data.setup_at === null) {
            navigate(Constants.ONBOARDINGV2);
            return;
          }

          setOnBoardingDate(new Date(data.setup_at));
        })
    }
  }, [navigate, user]);

  useEffect(() => {
    setSystemManager(null)
    setFocusLastDay(false)
  }, [option]);

  useEffect(() => {
    setSelectedDates(rangeDays)
  }, [rangeDays]);

  return (
    <FilterContext.Provider
      value={
        {
          farmFilter: farm,
          setFarmFilter: setFarm,
          areaFilter: area,
          microAreaFilter: microArea,
          setAreaFilter: setArea,
          sideBarOptionSelected: option,
          setSideBarOptionSelected: setOption,
          defaultBGFarm,
          setMicroArea,
          hideMicroAreaFilter: areaFilter,
          hideBatchFilter: lotsFilter,
          setHideMicroAreaFilter: setAreaFilter,
          setHideBatchFilter: setLotsFilter,
          microAreaDefaultOption,
          setMicroAreaDefaultOption,
          setBatchFilter: setBatch,
          batchFilter: batch,
          setBatchDefaultOption,
          batchDefaultOption,
          modalCalendarFilter,
          setModalCalendarFilter,
          systemManager,
          setSystemManager,
          dashboard,
          setDashboard,
          rangeDays,
          setRangeDays,
          selectedDates,
          setSelectedDates,
          balanceAnimal,
          setBalanceAnimal,
          balance,
          setBalance,
          balanceCapacity,
          setBalanceCapacity,
          areasIdsFilter,
          setAreasIdsFilter,
          farmIdsFilter,
          setFarmIdsFilter,
          focusLastDay,
          setFocusLastDay,
          viewRangeDays,
          setViewRangeDays,
          onBoardingDate,
          setOnBoardingDate,
          selectRangeDate,
          setSelectRangeDate,
          snapshot,
          setSnapshot,
          snapshotMicroAreaMetrics,
          setSnapshotMicroAreaMetrics,
          snapshotMicroArea,
          setSnapshotMicroArea,
          batchCategory,
          setBatchCategory,
          reloadTasks,
          setReloadTasks,
          modalInventoryTaskStop,
          setModalInventoryTaskStop,
          pastureManagementHistory,
          setPastureManagementHistory,
          responseFarms,
          setResponseFarms,
          responseArea,
          setResponseArea,
          paddockMapCatalogLoadError,
          setPaddockMapCatalogLoadError,
        } as valuesContext
      }
    >
      {children}
    </FilterContext.Provider>
  );
};
